import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const FailoverSolutions = lazy(() => import("./pages/FailoverSolutions"));
const FixedWirelessSolutions = lazy(() => import("./pages/FixedWirelessSolutions"));
const BusinessInternetSolutions = lazy(() => import("./pages/BusinessInternetSolutions"));
const IoTSolutions = lazy(() => import("./pages/IoTSolutions"));
const POTSReplacementSolutions = lazy(() => import("./pages/POTSReplacementSolutions"));
const StarlinkBusinessInternet = lazy(() => import("./pages/StarlinkBusinessInternet"));
const HomeInternet = lazy(() => import("./pages/HomeInternet"));
const About = lazy(() => import("./pages/About"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BusinessCore = lazy(() => import("./pages/BusinessCore"));
const BusinessAdvanced = lazy(() => import("./pages/BusinessAdvanced"));
const BusinessFailover = lazy(() => import("./pages/BusinessFailover"));
const BusinessUltimate = lazy(() => import("./pages/BusinessUltimate"));
const CoreFailover = lazy(() => import("./pages/CoreFailover"));
const AdvancedFailover = lazy(() => import("./pages/AdvancedFailover"));
const MissionCritical = lazy(() => import("./pages/MissionCritical"));
const EnterpriseShield = lazy(() => import("./pages/EnterpriseShield"));
const AssetTracking = lazy(() => import("./pages/AssetTracking"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LLMText = lazy(() => import("./pages/LLMText"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const AdminResourceEditor = lazy(() => import("./pages/AdminResourceEditor"));
const ContactDetail = lazy(() => import("./pages/ContactDetail"));
const AppointmentsCalendar = lazy(() => import("./pages/AppointmentsCalendar"));
const AppointmentsList = lazy(() => import("./pages/AppointmentsList"));
const AdminCalendar = lazy(() => import("./pages/AdminCalendar"));
const AppointmentDetail = lazy(() => import("./pages/AppointmentDetail"));
const Activities = lazy(() => import("./pages/Activities"));
const Reschedule = lazy(() => import("./pages/Reschedule"));
const BecomePartner = lazy(() => import("./pages/BecomePartner"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const UserApprovals = lazy(() => import("./pages/UserApprovals"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>Loading...</div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            
            {/* 301 Redirects for old URLs */}
            <Route path="/all-technology-solutions" element={<Navigate to="/" replace />} />
            <Route path="/communications-solutions" element={<Navigate to="/" replace />} />
            <Route path="/software-as-a-service-saas" element={<Navigate to="/" replace />} />
            <Route path="/artificial-intelligence" element={<Navigate to="/" replace />} />
            <Route path="/managed-services" element={<Navigate to="/" replace />} />
            <Route path="/infrastructure-solutions" element={<Navigate to="/" replace />} />
            <Route path="/security-solutions" element={<Navigate to="/" replace />} />
            <Route path="/energy-solutions" element={<Navigate to="/" replace />} />
            <Route path="/hardware-and-logistics" element={<Navigate to="/" replace />} />
            
            <Route path="/failover-solutions" element={<FailoverSolutions />} />
            <Route path="/fixed-wireless-solutions" element={<FixedWirelessSolutions />} />
            <Route path="/business-internet-solutions" element={<BusinessInternetSolutions />} />
            <Route path="/internet-of-things-iot" element={<IoTSolutions />} />
            <Route path="/pots-replacement-solutions" element={<POTSReplacementSolutions />} />
            <Route path="/pots-replacement" element={<Navigate to="/pots-replacement-solutions" replace />} />
            <Route path="/starlink-business-internet" element={<StarlinkBusinessInternet />} />
            <Route path="/home-internet" element={<HomeInternet />} />
            <Route path="/about-us" element={<About />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/resources/:slug" element={<ResourceDetail />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="/business-core" element={<BusinessCore />} />
            <Route path="/business-advanced" element={<BusinessAdvanced />} />
            <Route path="/business-failover" element={<BusinessFailover />} />
            <Route path="/business-ultimate" element={<BusinessUltimate />} />
            <Route path="/core-failover" element={<CoreFailover />} />
            <Route path="/advanced-failover" element={<AdvancedFailover />} />
            <Route path="/mission-critical" element={<MissionCritical />} />
            <Route path="/enterprise-shield" element={<EnterpriseShield />} />
            <Route path="/asset-tracking-solutions" element={<AssetTracking />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/llm.txt" element={<LLMText />} />
            <Route path="/reschedule/:appointmentId" element={<Reschedule />} />
            <Route path="/become-a-partner" element={<BecomePartner />} />
            
            {/* Admin Login (Public) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Protected Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
            <Route path="/admin/contacts/:id" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
            <Route path="/admin/appointments" element={<ProtectedRoute><AppointmentsCalendar /></ProtectedRoute>} />
            <Route path="/admin/appointments/list" element={<ProtectedRoute><AppointmentsList /></ProtectedRoute>} />
            <Route path="/admin/calendar" element={<ProtectedRoute><AdminCalendar /></ProtectedRoute>} />
            <Route path="/admin/appointments/:id" element={<ProtectedRoute><AppointmentDetail /></ProtectedRoute>} />
            <Route path="/admin/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
            <Route path="/admin/user-approvals" element={<ProtectedRoute><UserApprovals /></ProtectedRoute>} />
            <Route path="/admin/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
            <Route path="/admin/resources" element={<ProtectedRoute><AdminResources /></ProtectedRoute>} />
            <Route path="/admin/resources/new" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
            <Route path="/admin/resources/edit/:id" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
